import { useNavigate } from '@tanstack/react-router';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import { session } from '@/shared/session/session';

import { useSwitchOrganization } from '../hooks/use-switch-organization';

/** Shown only when /me lists more than one organisation; switching re-issues the session there. */
export function OrganizationSwitcher() {
  const { t } = useTranslation('auth');
  const { me } = session.useSession();
  const navigate = useNavigate();
  const switchTo = useSwitchOrganization();
  const organizations = me?.organizations.filter((o) => o.status === 'active') ?? [];
  if (!me || organizations.length < 2) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground w-full justify-between px-2 font-normal group-data-[collapsible=icon]:hidden"
          aria-label={t('organizations.switch')}
          disabled={switchTo.isPending}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate text-xs">{me.organization.name}</span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>{t('organizations.label')}</DropdownMenuLabel>
        {organizations.map((organization) => {
          const current = organization.id === me.organization.id;
          return (
            <DropdownMenuItem
              key={organization.id}
              disabled={current}
              onSelect={() =>
                switchTo.mutate(organization.id, {
                  onSuccess: () => void navigate({ to: '/org', replace: true }),
                })
              }
            >
              <span className="grid flex-1 leading-tight">
                <span className="truncate">{organization.name}</span>
                <span className="text-muted-foreground text-xs">{organization.roleName}</span>
              </span>
              {current ? <Check className="size-4" aria-hidden /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
